import {
	Box,
	Checkbox,
	FormControlLabel,
	Grid,
	Typography,
	Button,
} from "@mui/material";
import React, { FC, useCallback } from "react";
import { useStyles } from "src/theme/styles";
import { useTranslation } from "react-i18next";
import {
	TextEntryPoint,
	WebchatEntryPointCustomizationMappingsContainerProps,
	WebChatEntryPointCustomField,
} from "src/types/textEntryPoints";
import ColorPickerField from "../colorPicker/ColorPickerField";
import { CustomTextField } from "../shared/CustomTextField";
import WebchatEntryPointLogoUpload from "./WebchatEntryPointLogoUpload";
import customColors from "src/utils/customColors";
import WebChatCustomFieldInput from "src/components/textEntryPoints/WebChatEntryPointCustomFieldInput";
import {
	FormikErrors,
	FieldArray,
	FieldArrayRenderProps,
	useFormikContext,
} from "formik";

const WebchatEntryPointCustomizationContainer: FC<WebchatEntryPointCustomizationMappingsContainerProps> =
	React.forwardRef((props, _) => {
		const mappingProps = props[
			"entryPointMappingsContainerProps"
		] as WebchatEntryPointCustomizationMappingsContainerProps;
		const classes = useStyles();
		const { t } = useTranslation();

		const { setFieldValue } = useFormikContext<TextEntryPoint>();

		const onUploadedLogoChange = useCallback(
			(fileName?: string) => {
				setFieldValue("logo", fileName);
			},
			[setFieldValue]
		);

		const handleAdding = (arrayHelpers: FieldArrayRenderProps) => {
			const newCustomField: WebChatEntryPointCustomField = {
				name: "",
				isRequired: false,
			};

			arrayHelpers.push(newCustomField);
		};

		return (
			<Box className={classes.entryPointSectionContainer}>
				<Grid item container xs={12} spacing={2}>
					<Grid
						item
						xs={12}
						className={classes.entryPointContainerInfo}
					>
						{t("TextEntryPoint.ChatCustomizationExplanations")}
					</Grid>
					<Grid container item xs={6} spacing={2}>
						<Grid
							item
							xs={12}
							className={classes.entryPointContainerInfo}
						>
							{t("TextEntryPoint.DarkModeColors")}
						</Grid>
						<Grid item xs={6}>
							<ColorPickerField
								label="Dark mode primary color"
								onChange={(value) =>
									setFieldValue("darkModePrimaryColor", value)
								}
								value={mappingProps.darkModePrimaryColor}
								defaultColor={
									customColors.webchatEntryPointDarkModePrimaryColor
								}
								id="darkModePrimaryColor"
								error={Boolean(
									mappingProps.errors.darkModePrimaryColor
								)}
								helperText={
									mappingProps.errors.darkModePrimaryColor
								}
							/>
						</Grid>
						<Grid item xs={6}>
							<ColorPickerField
								label="Dark mode secondary color"
								onChange={(value) => {
									setFieldValue(
										"darkModeSecondaryColor",
										value
									);
								}}
								value={mappingProps.darkModeSecondaryColor}
								defaultColor={
									customColors.webchatEntryPointDarkModeSecondaryColor
								}
								id="darkModeSecondaryColor"
								error={Boolean(
									mappingProps.errors.darkModeSecondaryColor
								)}
								helperText={
									mappingProps.errors.darkModeSecondaryColor
								}
							/>
						</Grid>
					</Grid>
					<Grid container item xs={6} spacing={2}>
						<Grid
							item
							xs={12}
							className={classes.entryPointContainerInfo}
						>
							{t("TextEntryPoint.LightModeColors")}
						</Grid>
						<Grid item xs={6}>
							<ColorPickerField
								label="Light mode primary color"
								onChange={(value) =>
									setFieldValue(
										"lightModePrimaryColor",
										value
									)
								}
								value={mappingProps.lightModePrimaryColor}
								defaultColor={
									customColors.webchatEntryPointLightModePrimaryColor
								}
								id="lightModePrimaryColor"
								error={Boolean(
									mappingProps.errors.lightModePrimaryColor
								)}
								helperText={
									mappingProps.errors.lightModePrimaryColor
								}
							/>
						</Grid>
						<Grid item xs={6}>
							<ColorPickerField
								label="Light mode secondary color"
								onChange={(value) =>
									setFieldValue(
										"lightModeSecondaryColor",
										value
									)
								}
								value={mappingProps.lightModeSecondaryColor}
								defaultColor={
									customColors.webchatEntryPointLightModeSecondaryColor
								}
								id="lightModeSecondaryColor"
								error={Boolean(
									mappingProps.errors.lightModeSecondaryColor
								)}
								helperText={
									mappingProps.errors.lightModeSecondaryColor
								}
							/>
						</Grid>
					</Grid>
					<Grid item xs={4}>
						<CustomTextField
							size="small"
							color="info"
							error={Boolean(
								mappingProps.touched.welcomeMessage &&
									mappingProps.errors.welcomeMessage
							)}
							fullWidth
							helperText={
								mappingProps.touched.welcomeMessage &&
								mappingProps.errors.welcomeMessage
							}
							label={t("TextEntryPoint.WelcomeMessage")}
							name="welcomeMessage"
							onBlur={mappingProps.handleBlur}
							onChange={mappingProps.handleChange}
							value={mappingProps.welcomeMessage}
							variant="outlined"
							data-testid="welcomeMessage"
						/>
					</Grid>
					<Grid item xs={4}>
						<CustomTextField
							size="small"
							color="info"
							error={Boolean(
								mappingProps.touched.topicName &&
									mappingProps.errors.topicName
							)}
							fullWidth
							helperText={
								mappingProps.touched.topicName &&
								mappingProps.errors.topicName
							}
							label={t("TextEntryPoint.TopicName")}
							name="topicName"
							onBlur={mappingProps.handleBlur}
							onChange={mappingProps.handleChange}
							value={mappingProps.topicName}
							variant="outlined"
							data-testid="topicName"
						/>
					</Grid>
					<Grid item xs={4}>
						<CustomTextField
							size="small"
							color="info"
							disabled={mappingProps.showAgentName}
							error={Boolean(
								mappingProps.touched.agentDisplayName &&
									mappingProps.errors.agentDisplayName
							)}
							fullWidth
							helperText={
								mappingProps.touched.agentDisplayName &&
								mappingProps.errors.agentDisplayName
							}
							label={t("TextEntryPoint.AgentDisplayName")}
							name="agentDisplayName"
							onBlur={mappingProps.handleBlur}
							onChange={mappingProps.handleChange}
							value={mappingProps.agentDisplayName}
							variant="outlined"
							data-testid="agentDisplayName"
						/>
					</Grid>
					<Grid item xs={4}>
						<FormControlLabel
							control={
								<Checkbox
									color="primary"
									checked={mappingProps.showAgentName}
									name="showAgentName"
									onChange={(
										event: React.ChangeEvent<HTMLInputElement>,
										checked: boolean
									) => {
										mappingProps.handleChange(
											event,
											checked
										);
									}}
								/>
							}
							color="info"
							label={
								<Typography color="textSecondary">
									{t("TextEntryPoint.ShowAgentName")}
								</Typography>
							}
							data-testid="showAgentName"
							labelPlacement="end"
						/>
					</Grid>
					<Grid
						item
						xs={12}
						className={classes.entryPointContainerInfo}
					>
						{t("TextEntryPoint.Logo")}
					</Grid>
					<Grid item xs={12}>
						<WebchatEntryPointLogoUpload
							onFileChange={onUploadedLogoChange}
							fileName={mappingProps.logo}
						/>
					</Grid>
					<Grid
						item
						xs={12}
						className={classes.entryPointContainerInfo}
					>
						{t("TextEntryPoint.CustomerInformation")}
					</Grid>
					<Grid
						container
						item
						xs={12}
						spacing={2}
						justifyContent="flex-start"
						alignItems="flex-start"
					>
						<Grid
							item
							sx={{
								display: "flex",
								flexDirection: "column",
								minWidth: 0,
								pr: 2,
							}}
						>
							<FormControlLabel
								control={
									<Checkbox
										color="primary"
										checked={mappingProps.requestEmail}
										name="requestEmail"
										onChange={(
											event: React.ChangeEvent<HTMLInputElement>,
											checked: boolean
										) => {
											mappingProps.handleChange(
												event,
												checked
											);
											if (!checked) {
												setFieldValue(
													"requireEmail",
													false
												);
											}
										}}
									/>
								}
								label={
									<Typography
										color="textSecondary"
										sx={{ whiteSpace: "nowrap" }}
									>
										{t("TextEntryPoint.RequestEmail")}
									</Typography>
								}
								labelPlacement="end"
								sx={{ mb: 1 }}
							/>
							<FormControlLabel
								control={
									<Checkbox
										color="primary"
										checked={mappingProps.requestName}
										name="requestName"
										onChange={(
											event: React.ChangeEvent<HTMLInputElement>,
											checked: boolean
										) => {
											mappingProps.handleChange(
												event,
												checked
											);
											if (!checked) {
												setFieldValue(
													"requireName",
													false
												);
											}
										}}
									/>
								}
								label={
									<Typography
										color="textSecondary"
										sx={{ whiteSpace: "nowrap" }}
									>
										{t("TextEntryPoint.RequestName")}
									</Typography>
								}
								labelPlacement="end"
							/>
						</Grid>
						<Grid
							item
							sx={{
								display: "flex",
								flexDirection: "column",
								minWidth: 0,
							}}
						>
							<FormControlLabel
								control={
									<Checkbox
										color="primary"
										checked={mappingProps.requireEmail}
										name="requireEmail"
										onChange={mappingProps.handleChange}
										disabled={!mappingProps.requestEmail}
									/>
								}
								label={
									<Typography
										color="textSecondary"
										sx={{ whiteSpace: "nowrap" }}
									>
										{t("TextEntryPoint.RequireEmail")}
									</Typography>
								}
								labelPlacement="end"
								sx={{ mb: 1 }}
							/>
							<FormControlLabel
								control={
									<Checkbox
										color="primary"
										checked={mappingProps.requireName}
										name="requireName"
										onChange={mappingProps.handleChange}
										disabled={!mappingProps.requestName}
									/>
								}
								label={
									<Typography
										color="textSecondary"
										sx={{ whiteSpace: "nowrap" }}
									>
										{t("TextEntryPoint.RequireName")}
									</Typography>
								}
								labelPlacement="end"
							/>
						</Grid>
					</Grid>
					<Grid item xs={12}>
						<FieldArray
							name="customFields"
							render={(arrayHelpers) => (
								<Box>
									<Grid
										container
										direction="column"
										spacing={2}
										sx={{ paddingRight: "32px" }}
									>
										{mappingProps.customFields?.map(
											(x, index) => (
												<WebChatCustomFieldInput
													customField={x}
													index={index}
													errors={
														mappingProps.errors
															?.customFields
															? (mappingProps
																	.errors
																	?.customFields[
																	index
															  ] as FormikErrors<WebChatEntryPointCustomField>)
															: null
													}
													touched={
														mappingProps.touched
															?.customFields
															? mappingProps
																	.touched
																	?.customFields[
																	index
															  ]
															: null
													}
													handleBlur={
														mappingProps.handleBlur
													}
													handleChange={
														mappingProps.handleChange
													}
													handleDelete={() =>
														arrayHelpers.remove(
															index
														)
													}
												/>
											)
										)}
									</Grid>
									<Button
										sx={{ marginTop: 2 }}
										variant="contained"
										onClick={() =>
											handleAdding(arrayHelpers)
										}
										data-testid="addNewUrlButton"
									>
										{t("MessagingQueue.AddCustomField")}
									</Button>
								</Box>
							)}
						/>
					</Grid>
				</Grid>
			</Box>
		);
	});

export default WebchatEntryPointCustomizationContainer;
